/**
 * The deployment brand tier (WS3) that the interim root reads at render, held for the life of the
 * document.
 *
 * `loadDeploymentBranding` of `@zion/surface-host` reads and validates `/branding.json` (the Helm
 * ConfigMap the nginx serves). The boot's `prepareDocument` runs it once and hands the answer to
 * {@link rememberDeploymentBranding}, so the sync readers (the chrome binding and the interim
 * root's `BrandingProvider`) read it here without a thread through React. Precedence is the
 * callers': per-org, then deployment, then the hardwired neutral. A missing, invalid or empty file
 * reads `null`, the hardwired neutral.
 *
 * The shell's own copy of the loader went with WS-16d (R-125); this module keeps the value only.
 */
import type { DeploymentBranding } from '@zion/sdk-cp-dp-contracts';

let cached: DeploymentBranding | null = null;

/** The remembered deployment brand, or `null` before {@link rememberDeploymentBranding} ran or when it held `null`. Synchronous. */
export function getDeploymentBranding(): DeploymentBranding | null {
  return cached;
}

/**
 * Remember the deployment brand for the sync readers.
 *
 * Callers: `prepareDocument` once per boot, with the answer of `loadDeploymentBranding`, which
 * already parsed it with `DeploymentBrandingSchema`; and tests, which reset it with `null`.
 *
 * @param value The validated brand, or `null` for the neutral tier.
 * @returns `value`, so the call can end a promise chain. A later call replaces the value. It sends
 *   nothing and never throws.
 */
export function rememberDeploymentBranding(value: DeploymentBranding | null): DeploymentBranding | null {
  cached = value;
  return value;
}
